import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import '../styles/ElevationChart.css';

/**
 * ElevationChart - Stelvio-style elevation profile with gradient-based coloring
 * Maintains hover functionality to update map position
 */

// Color scale based on gradient percentage (Stelvio style)
const getGradientColor = (gradient) => {
  const g = gradient;
  if (g > 12) return '#e65c00';      // 12%+ - Orange (molt dur)
  if (g > 9) return '#3a3a3a';       // 9-11.9% - Very dark grey
  if (g > 6) return '#5a5a5a';       // 6-8.9% - Dark grey
  if (g > 3) return '#7a7a7a';       // 3-5.9% - Medium grey
  if (g >= 0) return '#a0a0a0';      // 0-2.9% - Light grey
  if (g > -5) return '#7cb87c';      // Baixada suau - Light green
  if (g > -10) return '#4a9f4a';     // Baixada moderada - Green
  return '#2d7d2d';                   // Baixada forta - Dark green
};

const ElevationChart = ({ coordinates, mapRef, pois = [] }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [hoveredPoi, setHoveredPoi] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 300 });

  // Process coordinates into chart data
  const chartData = useMemo(() => {
    if (!coordinates || coordinates.length === 0) return null;

    const data = [];
    let totalDistance = 0;
    
    for (let i = 0; i < coordinates.length; i++) {
      const point = coordinates[i];
      
      if (i > 0) {
        const prev = coordinates[i - 1];
        const R = 6371000; // metres
        const dLat = (point.lat - prev.lat) * Math.PI / 180;
        const dLon = (point.lon - prev.lon) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(prev.lat * Math.PI / 180) * Math.cos(point.lat * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        totalDistance += R * c;
      }
      
      // Calcula pendent (%) amb finestra de 5 punts
      let grade = 0;
      if (i >= 5 && i < coordinates.length - 5) {
        const window = 5;
        const elevDiff = coordinates[i + window].ele - coordinates[i - window].ele;
        let distDiff = 0;
        for (let j = i - window; j < i + window; j++) {
          const p1 = coordinates[j];
          const p2 = coordinates[j + 1];
          const R = 6371000;
          const dLat = (p2.lat - p1.lat) * Math.PI / 180;
          const dLon = (p2.lon - p1.lon) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          distDiff += R * c;
        }
        if (distDiff > 0) {
          grade = (elevDiff / distDiff) * 100;
        }
      }
      
      data.push({
        distance: totalDistance / 1000,
        elevation: point.ele || 0,
        grade: grade,
        lat: point.lat,
        lon: point.lon,
        index: i
      });
    }

    return data;
  }, [coordinates]);

  // Calculate chart geometry
  const chartGeometry = useMemo(() => {
    if (!chartData || chartData.length < 2) return null;

    const padding = { top: 40, right: 50, bottom: 50, left: 10 };
    const chartWidth = dimensions.width - padding.left - padding.right;
    const chartHeight = dimensions.height - padding.top - padding.bottom;

    const minElev = Math.min(...chartData.map(d => d.elevation)) - 30;
    const maxElev = Math.max(...chartData.map(d => d.elevation)) + 30;
    const maxDist = Math.max(...chartData.map(d => d.distance));

    const xScale = (d) => padding.left + (d / maxDist) * chartWidth;
    const yScale = (e) => padding.top + chartHeight - ((e - minElev) / (maxElev - minElev)) * chartHeight;

    // Generate points
    const points = chartData.map(d => ({
      x: xScale(d.distance),
      y: yScale(d.elevation),
      ...d
    }));

    // Sample points for segments (every N points for performance)
    const sampleRate = Math.max(1, Math.floor(chartData.length / 150));
    const sampledIndices = [];
    for (let i = 0; i < chartData.length; i += sampleRate) {
      sampledIndices.push(i);
    }
    if (sampledIndices[sampledIndices.length - 1] !== chartData.length - 1) {
      sampledIndices.push(chartData.length - 1);
    }

    // Generate area segments with gradient-based colors
    const segments = [];
    for (let i = 0; i < sampledIndices.length - 1; i++) {
      const idx1 = sampledIndices[i];
      const idx2 = sampledIndices[i + 1];
      const p1 = points[idx1];
      const p2 = points[idx2];
      
      // Average gradient for this segment
      let avgGrade = 0;
      let count = 0;
      for (let j = idx1; j <= idx2; j++) {
        avgGrade += chartData[j].grade;
        count++;
      }
      avgGrade = avgGrade / count;
      
      const color = getGradientColor(avgGrade);
      const baseY = yScale(minElev);
      
      segments.push({
        path: `M ${p1.x} ${baseY} L ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p2.x} ${baseY} Z`,
        color,
        gradient: avgGrade,
        midX: (p1.x + p2.x) / 2,
      });
    }

    // Generate line path
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    // Y axis ticks
    const range = maxElev - minElev;
    const step = Math.ceil(range / 5 / 50) * 50;
    const yTicks = [];
    for (let e = Math.ceil(minElev / step) * step; e <= maxElev; e += step) {
      yTicks.push(e);
    }

    // X axis ticks
    const xStep = Math.ceil(maxDist / 8);
    const xTicks = [];
    for (let d = 0; d <= maxDist; d += xStep) {
      xTicks.push(d);
    }
    if (xTicks[xTicks.length - 1] < maxDist) {
      xTicks.push(Math.ceil(maxDist));
    }

    return {
      points,
      segments,
      linePath,
      xScale,
      yScale,
      yTicks,
      xTicks,
      minElev,
      maxElev,
      maxDist,
      padding,
      chartWidth,
      chartHeight,
    };
  }, [chartData, dimensions]);

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width || 800,
          height: 300
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Handle mouse move on SVG
  const handleMouseMove = useCallback((event) => {
    if (!chartGeometry || !svgRef.current) return;

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    
    const { points, padding, chartWidth, maxDist } = chartGeometry;
    
    // Convert mouse X to distance
    const distanceAtMouse = ((mouseX - padding.left) / chartWidth) * maxDist;
    
    // Find closest point
    let closestPoint = null;
    let minDiff = Infinity;
    
    for (const point of points) {
      const diff = Math.abs(point.distance - distanceAtMouse);
      if (diff < minDiff) {
        minDiff = diff;
        closestPoint = point;
      }
    }
    
    if (closestPoint && distanceAtMouse >= 0 && distanceAtMouse <= maxDist) {
      setHoveredPoint(closestPoint);
      
      // Update map position
      if (mapRef?.current?.updateHoverPosition) {
        mapRef.current.updateHoverPosition(closestPoint.distance, coordinates);
      }
    }
  }, [chartGeometry, mapRef, coordinates]);

  const handleMouseLeave = useCallback(() => {
    setHoveredPoint(null);
    setHoveredPoi(null);
    if (mapRef?.current?.updateHoverPosition) {
      mapRef.current.updateHoverPosition(null);
    }
  }, [mapRef]);

  if (!chartData || !chartGeometry) {
    return (
      <div className="elevation-chart-container">
        <div className="elevation-chart-loading">Carregant perfil d'elevació...</div>
      </div>
    );
  }

  const { 
    segments, linePath, yTicks, xTicks, 
    xScale, yScale, minElev, padding 
  } = chartGeometry;

  return (
    <div className="elevation-chart-container" ref={containerRef}>
      {/* Legend */}
      <div className="gradient-legend stelvio-legend">
        <span className="legend-label">GRADIENT:</span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#a0a0a0'}}></span>
          0-3%
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#7a7a7a'}}></span>
          3-6%
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#5a5a5a'}}></span>
          6-9%
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#3a3a3a'}}></span>
          9-12%
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#e65c00'}}></span>
          &gt;12%
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#4a9f4a'}}></span>
          Baixada
        </span>
      </div>

      {/* SVG Chart */}
      <svg 
        ref={svgRef}
        width={dimensions.width} 
        height={dimensions.height}
        className="elevation-svg"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Grid lines */}
        <g className="grid-lines">
          {yTicks.map(tick => (
            <line
              key={tick}
              x1={padding.left}
              x2={dimensions.width - padding.right}
              y1={yScale(tick)}
              y2={yScale(tick)}
              stroke="#e5e5e5"
              strokeDasharray="3,3"
            />
          ))}
        </g>

        {/* Area segments with gradient colors */}
        <g className="elevation-segments">
          {segments.map((seg, i) => (
            <path
              key={i}
              d={seg.path}
              fill={seg.color}
              stroke="none"
              opacity="0.9"
            />
          ))}
        </g>

        {/* Elevation line */}
        <path
          d={linePath}
          fill="none"
          stroke="#2a2a2a"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />

        {/* X axis labels */}
        <g className="x-axis">
          {xTicks.map(tick => (
            <g key={tick}>
              <text
                x={xScale(tick)}
                y={dimensions.height - padding.bottom + 20}
                textAnchor="middle"
                fontSize="11"
                fill="#666"
              >
                {tick}
              </text>
              <line
                x1={xScale(tick)}
                y1={dimensions.height - padding.bottom}
                x2={xScale(tick)}
                y2={dimensions.height - padding.bottom + 5}
                stroke="#999"
              />
            </g>
          ))}
          <text
            x={dimensions.width / 2}
            y={dimensions.height - 8}
            textAnchor="middle"
            fontSize="12"
            fill="#666"
            fontWeight="500"
          >
            Distància (km)
          </text>
        </g>

        {/* Y axis labels */}
        <g className="y-axis">
          {yTicks.map(tick => (
            <text
              key={tick}
              x={dimensions.width - padding.right + 8}
              y={yScale(tick) + 4}
              fontSize="11"
              fill="#666"
            >
              {tick}
            </text>
          ))}
          <text
            x={dimensions.width - 20}
            y={padding.top - 10}
            textAnchor="middle"
            fontSize="11"
            fill="#666"
            fontWeight="500"
          >
            m
          </text>
        </g>

        {/* Hover indicator - hide when POI is hovered */}
        {hoveredPoint && !hoveredPoi && (
          <g className="hover-indicator">
            {/* Vertical line */}
            <line
              x1={hoveredPoint.x}
              y1={yScale(minElev)}
              x2={hoveredPoint.x}
              y2={hoveredPoint.y}
              stroke="#e65c00"
              strokeWidth="2"
              strokeDasharray="4,2"
            />
            
            {/* Point marker */}
            <circle
              cx={hoveredPoint.x}
              cy={hoveredPoint.y}
              r="6"
              fill="#e65c00"
              stroke="white"
              strokeWidth="2"
            />
            
            {/* Tooltip box */}
            <g transform={`translate(${
              hoveredPoint.x > dimensions.width - 120 ? hoveredPoint.x - 110 : hoveredPoint.x + 10
            }, ${
              hoveredPoint.y < 80 ? hoveredPoint.y + 10 : hoveredPoint.y - 70
            })`}>
              <rect
                x="0"
                y="0"
                width="100"
                height="58"
                fill="white"
                stroke="#333"
                strokeWidth="1"
                rx="4"
                filter="drop-shadow(0 2px 4px rgba(0,0,0,0.15))"
              />
              <text x="10" y="18" fontSize="12" fontWeight="bold" fill="#333">
                {hoveredPoint.elevation.toFixed(0)} m
              </text>
              <text x="10" y="34" fontSize="11" fill="#666">
                km {hoveredPoint.distance.toFixed(1)}
              </text>
              <text x="10" y="50" fontSize="11" fill={hoveredPoint.grade > 0 ? '#dc2626' : '#059669'}>
                {hoveredPoint.grade > 0 ? '↗' : '↘'} {hoveredPoint.grade.toFixed(1)}%
              </text>
            </g>
          </g>
        )}

        {/* POI markers - Icon only with hover tooltip */}
        {pois && pois.length > 0 && pois.map((poi, i) => {
          let poiX, poiY, elevation;
          
          // Use distance_from_start if available (more accurate)
          if (poi.distance_from_start !== undefined) {
            poiX = chartGeometry.xScale(poi.distance_from_start);
            
            // Find elevation at this distance
            let closestPoint = chartGeometry.points[0];
            let minDiff = Infinity;
            for (const point of chartGeometry.points) {
              const diff = Math.abs(point.distance - poi.distance_from_start);
              if (diff < minDiff) {
                minDiff = diff;
                closestPoint = point;
              }
            }
            poiY = closestPoint.y;
            elevation = closestPoint.elevation;
          } else {
            // Fallback: find closest point by coordinates
            let closestPoint = chartGeometry.points[0];
            let minDist = Infinity;
            
            for (const point of chartGeometry.points) {
              const dist = Math.sqrt(
                Math.pow(point.lat - poi.lat, 2) + 
                Math.pow(point.lon - poi.lon, 2)
              );
              if (dist < minDist) {
                minDist = dist;
                closestPoint = point;
              }
            }
            poiX = closestPoint.x;
            poiY = closestPoint.y;
            elevation = closestPoint.elevation;
          }

          // Skip if outside chart bounds
          if (poiX < padding.left || poiX > dimensions.width - padding.right) {
            return null;
          }
          
          // Get POI icon based on type
          const getPoiIcon = (type) => {
            const t = type?.toLowerCase() || '';
            if (t.includes('water') || t.includes('font') || t.includes('drinking')) return '💧';
            if (t.includes('restaurant') || t.includes('bar') || t.includes('cafe')) return '🍽️';
            if (t.includes('viewpoint') || t.includes('mirador')) return '👁️';
            if (t.includes('summit') || t.includes('peak') || t.includes('cim')) return '🔺';
            if (t.includes('parking')) return '🅿️';
            if (t.includes('refuge') || t.includes('refugi')) return '🏠';
            if (t.includes('shop') || t.includes('supermarket')) return '🛒';
            if (t.includes('hotel') || t.includes('hostel')) return '🛏️';
            if (t.includes('picnic') || t.includes('area')) return '🧺';
            return '📍';
          };
          
          const icon = getPoiIcon(poi.type);
          const name = poi.name || poi.type || 'POI';
          const elevText = elevation?.toFixed(0) || '';
          const isHovered = hoveredPoi === i;
          
          // Tooltip position - adjust if near edges
          const tooltipWidth = 140;
          let tooltipX = 0;
          if (poiX < padding.left + tooltipWidth/2 + 20) {
            tooltipX = -poiX + padding.left + 10;
          } else if (poiX > dimensions.width - padding.right - tooltipWidth/2 - 20) {
            tooltipX = dimensions.width - padding.right - poiX - tooltipWidth/2 - 10;
          }

          return (
            <g key={`poi-${i}`} className="poi-marker">
              {/* Clickable/hoverable area */}
              <g
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredPoi(i)}
                onMouseLeave={() => setHoveredPoi(null)}
              >
                {/* Invisible larger hit area */}
                <circle
                  cx={poiX}
                  cy={poiY}
                  r="15"
                  fill="transparent"
                />
                
                {/* Point marker on elevation line */}
                <circle
                  cx={poiX}
                  cy={poiY}
                  r={isHovered ? "7" : "5"}
                  fill="#e65c00"
                  stroke="white"
                  strokeWidth="2"
                  style={{ transition: 'r 0.15s ease' }}
                />
                
                {/* Icon above the point */}
                <text
                  x={poiX}
                  y={poiY - 12}
                  textAnchor="middle"
                  fontSize="14"
                  style={{ 
                    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))',
                    transition: 'font-size 0.15s ease'
                  }}
                >
                  {icon}
                </text>
              </g>
              
              {/* Tooltip - only visible on hover */}
              {isHovered && (
                <g 
                  className="poi-tooltip"
                  transform={`translate(${poiX + tooltipX}, ${poiY - 55})`}
                  style={{ pointerEvents: 'none' }}
                >
                  {/* Tooltip arrow */}
                  <polygon
                    points="0,38 -6,30 6,30"
                    fill="white"
                    stroke="#e65c00"
                    strokeWidth="1.5"
                    transform={`translate(${-tooltipX}, 0)`}
                  />
                  
                  {/* Background rectangle */}
                  <rect
                    x={-tooltipWidth/2}
                    y="-5"
                    width={tooltipWidth}
                    height="38"
                    fill="white"
                    stroke="#e65c00"
                    strokeWidth="1.5"
                    rx="4"
                    filter="drop-shadow(0 2px 4px rgba(0,0,0,0.15))"
                  />
                  
                  {/* POI name */}
                  <text
                    x="0"
                    y="10"
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="bold"
                    fill="#333"
                  >
                    {name.length > 20 ? name.substring(0, 20) + '...' : name}
                  </text>
                  
                  {/* Elevation and icon */}
                  <text
                    x="0"
                    y="25"
                    textAnchor="middle"
                    fontSize="10"
                    fill="#666"
                  >
                    {elevText}m · {poi.type} {icon}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default ElevationChart;